import React, { useState } from 'react';
import { ChevronDown, ChevronUp, BookOpen, GraduationCap } from 'lucide-react';

const TopicsSection = () => {
  const [expandedGrade, setExpandedGrade] = useState(null);

  const topics = {
    grade1: {
      title: "Grade 1",
      subtitle: "Foundation Topics",
      tier: "Foundation",
      topics: [
        "Addition and Subtraction",
        "Multiplication and Division",
        "Time",
        "Metric Conversions",
        "Writing, Simplifying and Ordering Fractions",
        "Place Value",
        "Rounding",
        "Negative Numbers",
        "Powers and Roots",
        "BIDMAS",
        "Factors and Multiples",
        "Coordinates",
        "Pictograms"
      ]
    },
    grade2: {
      title: "Grade 2",
      subtitle: "Foundation Topics",
      tier: "Foundation",
      topics: [
        "Calculation Problems",
        "Using a Calculator",
        "Systematic Listing",
        "Fractions of an Amount",
        "Fractions, Decimals and Percentages",
        "Simplifying Algebra",
        "Writing an Expression",
        "Function Machines",
        "Solving One Step Equations",
        "Angles",
        "Area and Perimeter",
        "Probability",
        "Frequency Polygons",
        "Averages (Mean, Median, Mode, Range)",
        "Bar Charts",
        "Stem and Leaf Diagrams",
        "Pie Charts"
      ]
    },
    grade3: {
      title: "Grade 3",
      subtitle: "Foundation Topics",
      tier: "Foundation",
      topics: [
        "Error Intervals",
        "Fractions",
        "Estimating",
        "Writing and Simplifying Ratio",
        "Ratio",
        "Proportion",
        "Percentages",
        "Percentage Change",
        "Exchange Rates",
        "Conversions and Units",
        "Scale Drawings",
        "Best Buy Questions",
        "Substitution",
        "Solving Equations",
        "Drawing Linear Graphs",
        "Area and Circumference of Circles",
        "Transformations (Rotations, Reflections, Enlargements, Translations)",
        "Area of Compound Shapes",
        "Frequency Trees",
        "Two Way Tables"
      ]
    },
    grade4: {
      title: "Grade 4",
      subtitle: "Foundation & Higher Topics",
      tier: "Foundation & Higher",
      topics: [
        "Compound Interest and Depreciation",
        "Indices",
        "Prime Factors, HCF and LCM",
        "Real Life and Distance Time Graphs",
        "Inequalities",
        "Forming and Solving Equations",
        "Sequences (Nth Term)",
        "Expanding and Factorising",
        "Pythagoras",
        "Angles in Parallel Lines",
        "Angles in Polygons",
        "Surface Area",
        "Volume of a Prism",
        "Cylinders (Volume and Surface Area)",
        "Loci and Construction",
        "Bearings",
        "Plans and Elevations",
        "Averages from Frequency Tables",
        "Probability and Relative Frequency",
        "Scatter Graphs"
      ]
    },
    grade5: {
      title: "Grade 5",
      subtitle: "Foundation & Higher Topics",
      tier: "Foundation & Higher",
      topics: [
        "Writing a Ratio as a Fraction or Linear Function",
        "Direct and Inverse Proportion",
        "Reverse Percentages",
        "Standard Form",
        "Speed and Density (Compound Measures)",
        "Changing the Subject of a Formula",
        "Expanding and Factorising Quadratics",
        "Solving Quadratics",
        "Drawing Quadratic Graphs",
        "Drawing Other Graphs (Cubic/Reciprocal)",
        "Simultaneous Equations",
        "Solving Simultaneous Equations Graphically",
        "Midpoint of a Line Segment",
        "Gradient of a Line",
        "Equation of a Line",
        "Spheres and Cones",
        "Sector Areas and Arc Lengths",
        "Similar Shapes (Lengths)",
        "SOHCAHTOA (Trigonometry)",
        "Exact Trig Values",
        "Vectors",
        "Probability Trees",
        "Venn Diagrams"
      ]
    },
    grade6: {
      title: "Grade 6",
      subtitle: "Higher Tier Only",
      tier: "Higher",
      topics: [
        "Recurring Decimals to Fractions",
        "Fractional and Negative Indices",
        "The Product Rule for Counting",
        "Repeated Percentage Change",
        "Expanding Triple Brackets",
        "Parallel and Perpendicular Lines",
        "Inequalities on Graphs",
        "Similar Shapes (Area and Volume)",
        "Enlarging with Negative Scale Factors",
        "Circle Theorems",
        "Cumulative Frequency",
        "Box Plots",
        "Capture Recapture"
      ]
    },
    grade7: {
      title: "Grade 7",
      subtitle: "Higher Tier Only",
      tier: "Higher",
      topics: [
        "Surds",
        "Bounds",
        "Direct and Inverse Proportion",
        "Quadratic Formula",
        "Factorising Harder Quadratics",
        "Algebraic Fractions",
        "Rearranging Harder Formulae",
        "Trigonometric and Exponential Graphs",
        "Inverse and Composite Functions",
        "Iteration",
        "Finding the Area of Any Triangle",
        "The Sine Rule",
        "The Cosine Rule",
        "Congruent Triangles",
        "3D Pythagoras and Trigonometry",
        "Histograms",
        "Conditional Probability"
      ]
    },
    grade89: {
      title: "Grade 8/9",
      subtitle: "Higher Tier Only (Advanced)",
      tier: "Higher",
      topics: [
        "Quadratic Simultaneous Equations",
        "Transforming Graphs y=f(x)",
        "Proof",
        "Completing the Square",
        "The Nth Term of a Quadratic Sequence",
        "Quadratic Inequalities",
        "Velocity Time Graphs",
        "Proof of the Circle Theorems",
        "Perpendicular Lines and Equation of a Tangent",
        "Vectors Proof Questions",
        "Probability Equation Questions"
      ]
    }
  };

  const toggleGrade = (gradeKey) => {
    setExpandedGrade(expandedGrade === gradeKey ? null : gradeKey);
  };

  return (
    <section id="topics" style={{ padding: '5rem 1.5rem', background: 'var(--bg-page)' }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              background: 'var(--accent-wash)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <GraduationCap size={28} color="var(--accent-text)" />
            </div>
          </div>
          <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
            GCSE Maths Topics Covered
          </h2>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            Comprehensive coverage of all GCSE Maths topics from Grade 1 to 9. We cover Foundation Tier (Grades 1-5) and Higher Tier (Grades 4-9) for all major exam boards including AQA, Edexcel, OCR, and WJEC.
          </p>
        </div>

        {/* Topics Accordion */}
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {Object.entries(topics).map(([gradeKey, grade]) => {
            const isExpanded = expandedGrade === gradeKey;
            const tierColor = grade.tier === 'Foundation' ? 'var(--accent-secondary)' : 
                             grade.tier === 'Higher' ? 'var(--accent-primary)' : 
                             'var(--accent-text)';
            
            return (
              <div 
                key={gradeKey}
                className="product-card"
                style={{ 
                  marginBottom: '1rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: isExpanded ? `2px solid ${tierColor}` : '1px solid var(--border-light)'
                }}
                onClick={() => toggleGrade(gradeKey)}
              >
                {/* Header */}
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: `${tierColor}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <BookOpen size={24} color={tierColor} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 className="heading-3" style={{ marginBottom: '0.25rem' }}>
                        {grade.title}
                      </h3>
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                        <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>
                          {grade.subtitle}
                        </p>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: `${tierColor}20`,
                          color: tierColor,
                          fontSize: '0.75rem',
                          fontWeight: 600
                        }}>
                          {grade.topics.length} Topics
                        </span>
                      </div>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp size={24} color="var(--text-primary)" />
                  ) : (
                    <ChevronDown size={24} color="var(--text-secondary)" />
                  )}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div style={{ 
                    marginTop: '1.5rem', 
                    paddingTop: '1.5rem', 
                    borderTop: '1px solid var(--border-light)',
                    animation: 'slideDown 0.3s ease'
                  }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                      gap: '0.75rem'
                    }}>
                      {grade.topics.map((topic, index) => (
                        <div 
                          key={index}
                          style={{
                            padding: '0.75rem',
                            background: 'var(--bg-section)',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--accent-wash)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-section)'}
                        >
                          <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: tierColor,
                            marginTop: '6px',
                            flexShrink: 0
                          }} />
                          <span className="body-small" style={{ lineHeight: 1.4 }}>{topic}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div style={{
          marginTop: '3rem',
          maxWidth: '800px',
          margin: '3rem auto 0',
          padding: '1.5rem',
          background: 'var(--accent-wash)',
          border: '1px solid var(--accent-primary)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <p className="body-medium" style={{ marginBottom: '0.5rem' }}>
            <strong>All exam boards covered:</strong> AQA, Edexcel, OCR, and WJEC
          </p>
          <p className="body-small" style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Dr. Michael's experience as an IGCSE examiner ensures you learn exactly what examiners are looking for in each topic.
          </p>
        </div>

        {/* Referral Box */}
        <div style={{
          marginTop: '2rem',
          maxWidth: '800px',
          margin: '2rem auto 0',
          padding: '1.5rem',
          background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.15) 0%, var(--accent-wash) 100%)',
          border: '2px solid var(--accent-secondary)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <h3 className="heading-3" style={{ marginBottom: '1rem', color: '#0F172A' }}>
            🎯 Friendship Groups & Referral Rewards
          </h3>
          <p className="body-medium" style={{ marginBottom: '1rem', color: '#334155' }}>
            We encourage friendship groups who are motivated to excel in maths to sign up together for group tuition!
          </p>
          <div style={{
            padding: '1rem',
            background: 'white',
            borderRadius: '8px',
            border: '2px dashed var(--accent-secondary)'
          }}>
            <p className="body-medium" style={{ fontWeight: 600, color: '#C5A059', marginBottom: '0.5rem' }}>
              ⭐ Refer a Friend, Get a FREE Session!
            </p>
            <p className="body-small" style={{ color: '#334155', margin: 0 }}>
              For every new student you refer to Abba Academy, you'll receive one FREE session as our thank you.
            </p>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  );
};

export default TopicsSection;
